import { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from '@mui/material';
import { useDataProvider, useNotify, useRefresh } from 'react-admin';
import { useAccessContext } from '../auth/useAccessContext';
import { hasAccess } from '../auth/permissions';
import { formatMoney } from '../cashbacks/formatMoney';
import {
  CURRENCY_BY_MARKET,
  SUPPORT_REASON_CODES,
  type Market,
} from '../dataProvider/types';
import { SUPPORT_CREDIT_CAP, SUPPORT_REASON_LABEL } from './ticketsState';

/**
 * Credit a member's balance as a support agent (D-support-role, the Phase 6 bounded
 * credit). A BOUNDED, POSITIVE-ONLY specialisation of the Phase 5 ledger entry: the
 * same ledger-entry-create mechanism, but a LOWER cap, support reason codes, no debit
 * path, and the support capability (balances:credit, NOT the unbounded super_admin
 * balances:adjust).
 *
 * Value moves only through a recorded ledger entry the balance derives from: it creates
 * a balanceLedger row (kind support_credit, a POSITIVE amount, transactionId null) that
 * deriveBalanceSigned adds, and the balance re-derives. There is no balance field to
 * write and none is written. A mandatory reason code and note ride in the entry, so
 * both land in the audit trail (audited for free by the wrapper).
 *
 * GUARDRAIL: the SUPPORT cap (a lower threshold than the adjustment cap). A credit whose
 * amount reaches SUPPORT_CREDIT_CAP goes through a deliberate confirmation that shows the
 * figure before it commits. There is no separate override capability; the confirm IS the
 * friction. `refund` is a reason code here, an in-platform credit, NOT a cashout reversal.
 */
export const SupportCreditAction = ({
  user,
}: {
  user: { id: number; name: string; market: Market; balanceSigned?: number };
}) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const refresh = useRefresh();
  const { role } = useAccessContext();
  const [createdAtMs] = useState<number>(() => Date.now());

  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<'form' | 'confirm'>('form');
  const [loading, setLoading] = useState(false);
  const [amount, setAmount] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');

  const currency = CURRENCY_BY_MARKET[user.market];
  const cap = SUPPORT_CREDIT_CAP[user.market];
  // POSITIVE-ONLY: the amount is always a credit; there is no direction toggle.
  const amountNum = Math.round(Number(amount) * 100) / 100;
  const amountValid = Number.isFinite(amountNum) && amountNum > 0;
  const before = Number(user.balanceSigned ?? 0);
  const after = Math.round((before + amountNum) * 100) / 100;
  const overCap = amountValid && amountNum >= cap;
  const canSubmit =
    amountValid && reasonCode.trim().length > 0 && note.trim().length > 0;

  const reset = () => {
    setPhase('form');
    setAmount('');
    setReasonCode('');
    setNote('');
  };

  const close = () => {
    setOpen(false);
    setLoading(false);
    reset();
  };

  const doCreate = async () => {
    setLoading(true);
    try {
      if (
        !hasAccess(
          { role, accessibleRegions: [] },
          { capability: 'balances:credit' },
        )
      ) {
        throw new Error('Not permitted');
      }
      // The support credit: a POSITIVE signed ledger entry the balance derives from.
      // NOT a direct balance write.
      await dataProvider.create('balanceLedger', {
        data: {
          userId: user.id,
          transactionId: null,
          amount: amountNum,
          market: user.market,
          kind: 'support_credit',
          reasonCode,
          reason: note.trim(),
          createdAt: new Date(createdAtMs).toISOString(),
        },
      });
      notify('Member credited', { type: 'success' });
      close();
      refresh();
    } catch {
      notify('Action not permitted or failed', { type: 'error' });
      close();
    }
  };

  const onSubmit = () => {
    if (!canSubmit) return;
    if (overCap) {
      setPhase('confirm');
      return;
    }
    void doCreate();
  };

  return (
    <>
      <Button variant="outlined" color="success" onClick={() => setOpen(true)}>
        Credit member
      </Button>
      <Dialog open={open} onClose={close} fullWidth maxWidth="sm">
        <DialogTitle>Credit {user.name}</DialogTitle>
        {phase === 'form' ? (
          <>
            <DialogContent>
              <DialogContentText sx={{ mb: 2 }}>
                A support credit is a positive ledger entry the balance derives
                from, never a direct change to a balance and never a debit.
                Enter the amount, a reason and a note.
              </DialogContentText>
              <Box
                data-testid="support-credit-balance"
                sx={{ mb: 2, fontWeight: 600 }}
              >
                Current balance: {formatMoney(before, currency)}
              </Box>
              <TextField
                label="Credit amount"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
                error={amount.length > 0 && !amountValid}
                helperText={`In ${currency.toUpperCase()}. A credit at or above ${formatMoney(cap, currency)} needs a second confirmation.`}
                inputProps={{ 'data-testid': 'support-credit-amount' }}
              />
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id={`support-reason-${user.id}`}>
                  Reason code
                </InputLabel>
                <Select
                  labelId={`support-reason-${user.id}`}
                  label="Reason code"
                  value={reasonCode}
                  inputProps={{ 'data-testid': 'support-credit-reason' }}
                  onChange={(e) => setReasonCode(e.target.value)}
                >
                  {SUPPORT_REASON_CODES.map((code) => (
                    <MenuItem key={code} value={code}>
                      {SUPPORT_REASON_LABEL[code]}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                label="Justification"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={2}
                helperText="Recorded with the action, for the audit trail"
              />
              {amountValid && (
                <Box data-testid="support-credit-projected" sx={{ mt: 2 }}>
                  New balance after: {formatMoney(after, currency)}
                </Box>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={close}>Cancel</Button>
              <Button
                variant="contained"
                color="success"
                disabled={!canSubmit || loading}
                onClick={onSubmit}
              >
                Confirm
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogContent>
              <DialogContentText
                data-testid="support-credit-large-confirm"
                sx={{ mb: 2 }}
              >
                You are about to credit {user.name} by{' '}
                {formatMoney(amountNum, currency)}. The new balance will be{' '}
                {formatMoney(after, currency)}. This is at or above the support
                cap. Confirm it is correct.
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setPhase('form')}>Back</Button>
              <Button
                variant="contained"
                color="success"
                disabled={loading}
                onClick={() => void doCreate()}
              >
                Confirm large credit
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
};
